import React, { createContext, useContext, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import * as THREE from 'three';
import { useThree } from '@react-three/fiber';
import { blockMatrices } from '../lib/voxel-select';
import { buildPickGrid, pickCell } from '../lib/voxel-pick';

/**
 * Selections of the 3D models (lib/voxel-select.js, by model id) plus the outside opacity; provided
 * inside the Canvas by SubsurfaceViewer3D so the flat voxel model and the terrain-draped one draw the
 * same cells.
 */
export const VoxelSelectionContext = createContext({ models: {}, outsideOpacity: 0 });
export const useVoxelSelection = (id) => {
  const { models, outsideOpacity } = useContext(VoxelSelectionContext);
  return { sel: models[id] || null, outsideOpacity };
};

const noRaycast = () => null; // block hover is a grid lookup (lib/voxel-pick.js), not an O(n) instance raycast

/** Material hook: multiplies the fragment alpha by the per-vertex / per-instance `voxelAlpha` attribute. */
function applyVoxelAlpha(shader) {
  shader.vertexShader = shader.vertexShader
    .replace('#include <common>', '#include <common>\nattribute float voxelAlpha;\nvarying float vVoxelAlpha;')
    .replace('#include <begin_vertex>', '#include <begin_vertex>\nvVoxelAlpha = voxelAlpha;');
  shader.fragmentShader = shader.fragmentShader
    .replace('#include <common>', '#include <common>\nvarying float vVoxelAlpha;')
    .replace('#include <color_fragment>', '#include <color_fragment>\ndiffuseColor.a *= vVoxelAlpha;');
}
const alphaCacheKey = () => 'voxelAlpha';

/** Fill an InstancedMesh with the selected cells' matrices, colours and (translucent set) alphas. */
function fillInstances(mesh, indices, matrices, colors, alpha) {
  const n = indices.length;
  const m = mesh.instanceMatrix.array;
  const col = new Float32Array(n * 3);
  const al = alpha ? new Float32Array(n) : null;
  for (let k = 0; k < n; k++) {
    const i = indices[k];
    for (let j = 0; j < 16; j++) m[k * 16 + j] = matrices[i * 16 + j];
    col[k * 3] = colors[i * 3]; col[k * 3 + 1] = colors[i * 3 + 1]; col[k * 3 + 2] = colors[i * 3 + 2];
    if (al) al[k] = alpha[i];
  }
  mesh.instanceMatrix.needsUpdate = true;
  mesh.instanceColor = new THREE.InstancedBufferAttribute(col, 3);
  mesh.instanceColor.needsUpdate = true;
  if (al) mesh.geometry.setAttribute('voxelAlpha', new THREE.InstancedBufferAttribute(al, 1));
  mesh.count = n;
  mesh.frustumCulled = false;
}

const boxGeometry = new THREE.BoxGeometry(1, 1, 1);

/** One instanced mesh of unit boxes (a block per index); `alpha` (per cell) makes it the translucent, per-instance-alpha set. */
function BlockSet({ indices, matrices, colors, alpha, opacity, clippingPlanes }) {
  const ref = useRef(null);
  const n = indices.length;
  // translucent sets own their geometry (it carries the per-instance alpha attribute)
  const geometry = useMemo(() => (alpha ? boxGeometry.clone() : boxGeometry), [alpha]);
  useEffect(() => () => { if (alpha) geometry.dispose(); }, [alpha, geometry]);
  useLayoutEffect(() => {
    if (ref.current && n) fillInstances(ref.current, indices, matrices, colors, alpha);
  }, [indices, matrices, colors, alpha, n]);
  if (!n) return null;
  return (
    <instancedMesh key={n} ref={ref} args={[geometry, undefined, n]} renderOrder={alpha ? 1 : 0} raycast={noRaycast}>
      <meshLambertMaterial flatShading transparent={!!alpha || opacity < 1} opacity={opacity} depthWrite={!alpha}
        clippingPlanes={clippingPlanes} onBeforeCompile={alpha ? applyVoxelAlpha : undefined}
        customProgramCacheKey={alpha ? alphaCacheKey : undefined} />
    </instancedMesh>
  );
}

/** One points object over a subset of cells; `alpha` (per cell) makes it the translucent set. */
function PointSet({ indices, positions, colors, alpha, opacity, clippingPlanes }) {
  const geometry = useMemo(() => {
    const n = indices.length;
    const pos = new Float32Array(n * 3), col = new Float32Array(n * 3), al = alpha ? new Float32Array(n) : null;
    for (let k = 0; k < n; k++) {
      const i = indices[k];
      for (let j = 0; j < 3; j++) { pos[k * 3 + j] = positions[i * 3 + j]; col[k * 3 + j] = colors[i * 3 + j]; }
      if (al) al[k] = alpha[i];
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('color', new THREE.BufferAttribute(col, 3));
    if (al) g.setAttribute('voxelAlpha', new THREE.BufferAttribute(al, 1));
    return g;
  }, [indices, positions, colors, alpha]);
  useEffect(() => () => geometry.dispose(), [geometry]);
  if (!indices.length) return null;
  return (
    <points geometry={geometry} renderOrder={alpha ? 1 : 0}>
      <pointsMaterial size={0.8} vertexColors transparent={!!alpha || opacity < 1} opacity={opacity} depthWrite={!alpha} sizeAttenuation
        clippingPlanes={clippingPlanes} onBeforeCompile={alpha ? applyVoxelAlpha : undefined}
        customProgramCacheKey={alpha ? alphaCacheKey : undefined} />
    </points>
  );
}

/** Indices of the selection's cells split by opacity: alpha 1 opaque, 0 < alpha < 1 translucent, 0 skipped. */
export function splitByAlpha(sel) {
  const opaque = [], translucent = [];
  const alpha = sel.alpha;
  [sel.inside, sel.outside].forEach((list) => {
    for (let k = 0; k < list.length; k++) {
      const a = alpha[list[k]];
      if (a >= 1) opaque.push(list[k]); else if (a > 0) translucent.push(list[k]);
    }
  });
  return { opaque: Int32Array.from(opaque), translucent: Int32Array.from(translucent) };
}

/**
 * Pointer hover over the blocks: the camera ray through the pointer is marched through the model's cell grid
 * (lib/voxel-pick.js) and the hit point goes to `onHover`, whatever the number of blocks. The pick grid is
 * built on the first hover of a selection.
 */
function useBlockHover({ sel, positions, split, clippingPlanes, onHover, onHoverEnd, entry }) {
  const { gl, camera } = useThree();
  const cache = useRef({ key: null, pick: null, mask: null });
  const active = !!onHover && sel?.mode === 'blocks' && !!positions;
  useEffect(() => {
    if (!active) return undefined;
    const el = gl.domElement;
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    let raf = 0, last = null, over = false;
    const run = () => {
      raf = 0;
      if (!last) return;
      const c = cache.current;
      if (c.key !== sel || c.positions !== positions) {
        const mask = new Uint8Array(sel.cells.length);
        split.opaque.forEach((i) => { mask[i] = 1; });
        split.translucent.forEach((i) => { mask[i] = 1; });
        cache.current = { key: sel, positions, pick: buildPickGrid(sel.cells, positions), mask };
      }
      const { pick, mask } = cache.current;
      const rect = el.getBoundingClientRect();
      ndc.set(((last.x - rect.left) / rect.width) * 2 - 1, -((last.y - rect.top) / rect.height) * 2 + 1);
      raycaster.setFromCamera(ndc, camera);
      const hit = pick ? pickCell({ origin: raycaster.ray.origin.toArray(), dir: raycaster.ray.direction.toArray() }, pick, mask) : null;
      const p = hit && new THREE.Vector3(...hit.point);
      if (p && !(clippingPlanes || []).some((pl) => pl.distanceToPoint(p) < 0)) { over = true; onHover(entry, p); }
      else if (over) { over = false; onHoverEnd?.(); }
    };
    const move = (e) => { last = { x: e.clientX, y: e.clientY }; if (!raf) raf = requestAnimationFrame(run); };
    const leave = () => { last = null; if (over) { over = false; onHoverEnd?.(); } };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerleave', leave);
    return () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerleave', leave);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [active, gl, camera, sel, positions, split, clippingPlanes, onHover, onHoverEnd, entry]);
}

/**
 * A 3D model's selected cells as blocks or points: cells with alpha 1 opaque (fence opacity), cells
 * with 0 < alpha < 1 drawn after them with their own alpha (the opacity curve, times the outside-range
 * opacity outside the visible range), alpha 0 skipped (lib/voxel-select.js). `positions` is the scene
 * position of each valid cell (flat or hung from the terrain).
 */
export default function VoxelModel({ entry, sel, positions, opacity = 0.95, clippingPlanes, onHover, onHoverEnd }) {
  const matrices = useMemo(() => (sel?.mode === 'blocks' ? blockMatrices(sel.cells, positions) : null), [sel, positions]);
  const split = useMemo(() => (sel ? splitByAlpha(sel) : null), [sel]);
  useBlockHover({ sel, positions, split, clippingPlanes, onHover, onHoverEnd, entry });
  if (!sel) return null;
  const common = { colors: sel.colors, clippingPlanes };
  const Part = sel.mode === 'blocks' ? BlockSet : PointSet;
  const extra = sel.mode === 'blocks' ? { matrices } : { positions };
  return (
    <>
      <Part {...common} {...extra} indices={split.opaque} opacity={opacity} />
      <Part {...common} {...extra} indices={split.translucent} alpha={sel.alpha} opacity={1} />
    </>
  );
}
